import React, { CSSProperties } from 'react';
import { greys, mainColors } from '../../../../../styling/theme';
import { NavReconPerformanceSplit } from '../../../../../api/rs-hg/nav-recon/types';
import type { NavReconFundPipelineProgress } from '../pipelineMetadata';

interface Props {
  row: {
    overviewHasPayload: boolean;
    performance_split?: NavReconPerformanceSplit;
    pipelineProgress?: NavReconFundPipelineProgress;
    snapshot_timestamp?: string | null;
    fund_name_full?: string | null;
    position_date?: string | null;
    current_nav_date?: string | null;
  };
}

const panelStyle: CSSProperties = {
  padding: '1rem 1.5rem',
  background: mainColors.veryFaintBlue,
  border: `1px solid ${greys.grey300}`,
  borderRadius: '0.2rem',
};

const sectionStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(12rem, 1fr))',
  gap: '0.75rem',
  marginTop: '0.75rem',
};

const cardStyle: CSSProperties = {
  background: 'white',
  border: `1px solid ${greys.grey200}`,
  borderRadius: '0.38rem',
  padding: '0.75rem',
};

const labelStyle: CSSProperties = {
  color: greys.headerGrey,
  fontSize: '0.7rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  marginBottom: '0.25rem',
};

const valueStyle: CSSProperties = {
  color: greys.grey900,
  fontSize: '0.9rem',
  margin: 0,
};

const sectionTitleStyle: CSSProperties = {
  color: mainColors.mainBlue,
  fontSize: '0.9rem',
  fontWeight: 700,
  margin: '0.25rem 0 0',
};

const formatRawValue = (value: number | null | undefined) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return 'null';
  }
  return String(value);
};

const PERFORMANCE_SPLIT_FIELDS: {
  label: string;
  key: keyof NavReconPerformanceSplit;
}[] = [
  { label: 'Funding Swap Stocks', key: 'funding_swap_stocks' },
  { label: 'Certificate', key: 'certificate' },
  { label: 'Fees', key: 'fees' },
  { label: 'Put Option', key: 'put_option' },
  { label: 'CLO ETF', key: 'clo_etf' },
];

const DetailCard = ({ label, value }: { label: string; value: string }) => (
  <div style={cardStyle}>
    <div style={labelStyle}>{label}</div>
    <p style={valueStyle}>{value}</p>
  </div>
);

const pipelinePendingBannerStyle: CSSProperties = {
  marginBottom: '0.75rem',
  padding: '0.75rem 0.88rem',
  borderRadius: '0.38rem',
  border: `1px solid #fcd34d`,
  background: '#fffbeb',
  color: '#78350f',
  fontSize: '0.82rem',
  lineHeight: 1.45,
};

const NavReconOverviewDetailPanel: React.FC<Props> = ({ row }) => {
  if (!row.overviewHasPayload) {
    return (
      <div style={panelStyle}>
        <p style={{ margin: 0, fontSize: '0.85rem', color: greys.grey700 }}>
          No overview metrics are available for this fund yet.
        </p>
      </div>
    );
  }

  return (
    <div style={panelStyle}>
      {row.pipelineProgress?.status === 'pending' ? (
        <div style={pipelinePendingBannerStyle} role="status">
          <p style={{ margin: '0 0 0.35rem', fontWeight: 700 }}>
            This fund is still updating
          </p>
          <p style={{ margin: 0 }}>{row.pipelineProgress.displayLabel}</p>
        </div>
      ) : null}
      <p style={sectionTitleStyle}>Performance Split</p>
      <div style={sectionStyle}>
        {PERFORMANCE_SPLIT_FIELDS.map(({ label, key }) => (
          <DetailCard
            key={key}
            label={label}
            value={formatRawValue(row.performance_split?.[key])}
          />
        ))}
      </div>
    </div>
  );
};

export default NavReconOverviewDetailPanel;
